import DataHelper from 'framework/helpers/data'
import ConfigHelper from 'framework/helpers/config'
import { of, throwError } from 'rxjs'
import { mergeMap, map } from 'rxjs/operators'
import { dvrFactory, dvrQuotaFactory } from 'services/nagra/factories'
import VoltError from 'VoltError'
import Fetch from '../fetch'

export default class RecordingsApi extends Fetch {
    /**
     * Get all recordings
     *
     * @returns {Observable<Array>}
     */
    getEventRecordings = ({
        limit = this.config?.pageLimit?.recordings || 60,
        statuses = undefined,
        recordingIDs = undefined,
        eventId = undefined,
        language,
    } = {}) => {
        let offset = 0

        const getFilterById = () => {
            if (recordingIDs) {
                return {
                    filter:
                        recordingIDs.length === 1
                            ? `{${this._appendFilter('id', recordingIDs[0])}}`
                            : `{"id": {${this._appendFilterList(
                                  '$in',
                                  'id',
                                  recordingIDs,
                                  (_, val) => `"${val}"`
                              )}}}`,
                }
            } else if (eventId) {
                return { filter: JSON.stringify({ eventId }) }
            } else {
                return {}
            }
        }

        return this.recursiveFetch(
            (_, limit, offset) => {
                return this.fetch({
                    endpoint: '/cdvr/v2/recordings',
                    params: {
                        token: DataHelper.getInstance().getSecondaryAccessToken(),
                        page: offset / limit + 1,
                        limit,
                        statuses,
                        ...getFilterById(),
                    },
                    method: 'GET',
                })
            },
            { offset, limit, responseResource: 'recordingSet' }
        ).pipe(
            mergeMap((npvrs = []) => {
                if (!npvrs.length) {
                    return of([])
                }

                return of(
                    npvrs
                        .map((npvr) =>
                            dvrFactory(npvr, this.config.platform, this.config, language)
                        )
                        .filter(Boolean)
                )
            })
        )
    }

    /**
     * Get recordings by ids
     *
     * @returns {Observable<Array>}
     */
    getRecordingsData = ({ ids, isSeries }) => {
        if (isSeries) return of([])
        if (ids.length === 1) return this.getEventRecordings({ recordingIDs: ids[0] })
        return this.getEventRecordings({ recordingIDs: ids })
    }

    /**
     * Set event-based recording
     * @returns {Observable}
     */
    setEventRecording = ({ id }) =>
        this.fetch({
            endpoint: '/cdvr/v1/recordings',
            params: { token: DataHelper.getInstance().getSecondaryAccessToken() },
            body: {
                eventId: id,
            },
            method: 'POST',
        }).pipe(
            mergeMap(() =>
                this.getEventRecordings({ eventId: id, limit: 1 }).pipe(
                    map((recordings) => (recordings.length ? recordings[0] : {}))
                )
            )
        )

    deleteRecording = ({ id }) => {
        return this.fetch({
            endpoint: `/cdvr/v1/recordings/${id}`,
            params: { token: DataHelper.getInstance().getSecondaryAccessToken() },
            method: 'DELETE',
        }).pipe(mergeMap(() => of(true)))
    }

    cancelRecording = ({ id }) => {
        return this.fetch({
            endpoint: `/cdvr/v1/recordings/${id}`,
            params: { token: DataHelper.getInstance().getSecondaryAccessToken() },
            method: 'DELETE',
        }).pipe(mergeMap(() => of(true)))
    }

    /**
     * @returns {Observable}
     */
    modifyRecordingType = () => {
        return throwError(new VoltError(VoltError.codes.MISSING_API_METHOD_IMPLEMENTATION))
    }

    /**
     * Get recording storage space
     * @returns {Observable}
     */
    getStorage() {
        return this.fetch({
            endpoint: '/cdvr/v1/account/quotausage',
            params: {
                token: DataHelper.getInstance().getSecondaryAccessToken(),
            },
            method: 'GET',
        }).pipe(
            mergeMap(({ response } = {}) => {
                return of(dvrQuotaFactory(response))
            })
        )
    }

    /**
     * @returns {Observable<Array>}
     */
    getSeriesRecordings({ limit = 10, platform = 'android', language }) {
        const { disableSerieRecording = true } = ConfigHelper.getInstance().getConfig('nagra')
        if (disableSerieRecording) {
            return of([])
        }

        let offset = 0
        return this.recursiveFetch(
            (_, limit, offset) => {
                return this.fetch({
                    endpoint: '/cdvr/v1/seriesrecordings',
                    params: {
                        token: DataHelper.getInstance().getSecondaryAccessToken(),
                        page: offset / limit + 1,
                    },
                    method: 'GET',
                })
            },
            { offset, limit }
        ).pipe(
            mergeMap((response = []) => {
                if (!response.length) {
                    return of([])
                }

                return of(
                    response
                        .reduce(
                            (acc, val) => [
                                ...acc,
                                ...val.recordings.reduce((accR, valR) => [...accR, valR], []),
                            ],
                            []
                        )
                        .map((npvr) => dvrFactory(npvr, platform, this.config, language))
                        .filter(Boolean)
                )
            })
        )
    }
}
